import { useEffect, useMemo, useRef, useState } from 'react';

import { getRespelWarehouseList } from '../../../../helpers/warehouse/backend_helper';

const initialWh = () => ({
	loading: false,
	done: false,
	list: null,
	error: null,
});

/**
 * RESPEL 1+1: tras listar ingresos/stock, si la lista viene vacía consulta bodegas elegibles
 * para distinguir "sin bodega" vs "sin movimientos aún".
 *
 * @param {object} opts
 * @param {string} opts.establishmentId
 * @param {boolean} opts.establishmentReady
 * @param {Array} opts.list
 * @param {boolean} opts.isLoadingList
 * @param {unknown} opts.listError
 * @param {number} [opts.feedItemCount] — si se define (p. ej. total remoto del feed), sustituye a ``list.length`` para el 1+1 y el botón Crear.
 */
export function useRespelEstablishmentToolbarGate({
	establishmentId,
	establishmentReady,
	list,
	isLoadingList,
	listError,
	feedItemCount,
}) {
	const effectiveItemCount =
		typeof feedItemCount === 'number' ? feedItemCount : list.length;
	const hasListError = useMemo(() => {
		if (listError == null || listError === false) return false;
		if (typeof listError === 'string') return listError.length > 0;
		if (typeof listError === 'object') return true;
		return Boolean(listError);
	}, [listError]);

	const [wh, setWh] = useState(initialWh);
	const [listSettledEpoch, setListSettledEpoch] = useState(0);
	const prevLoading = useRef(isLoadingList);

	useEffect(() => {
		setListSettledEpoch(0);
		prevLoading.current = false;
		setWh(initialWh());
	}, [establishmentId]);

	useEffect(() => {
		if (!establishmentReady) return;
		const was = prevLoading.current;
		prevLoading.current = isLoadingList;
		if (was && !isLoadingList) {
			setListSettledEpoch(e => e + 1);
		}
	}, [establishmentReady, isLoadingList]);

	useEffect(() => {
		if (!establishmentReady) return;
		if (isLoadingList) {
			setWh(initialWh());
		}
	}, [establishmentReady, isLoadingList]);

	useEffect(() => {
		if (!establishmentReady || !establishmentId) return;
		if (isLoadingList) return;
		if (hasListError) return;
		if (listSettledEpoch === 0) return;
		if (effectiveItemCount > 0) return;

		let cancelled = false;
		setWh(() => ({ loading: true, done: false, list: null, error: null }));
		getRespelWarehouseList(establishmentId)
			.then(rows => {
				if (cancelled) return;
				setWh({
					loading: false,
					done: true,
					list: Array.isArray(rows) ? rows : [],
					error: null,
				});
			})
			.catch(err => {
				if (cancelled) return;
				setWh({
					loading: false,
					done: true,
					list: null,
					error:
						err?.message ||
						'No se pudo verificar bodegas RESPEL.',
				});
			});
		return () => {
			cancelled = true;
		};
	}, [
		establishmentReady,
		establishmentId,
		isLoadingList,
		hasListError,
		effectiveItemCount,
		listSettledEpoch,
	]);

	const resolutionComplete = useMemo(() => {
		if (!establishmentReady) return false;
		if (isLoadingList || hasListError) return false;
		if (listSettledEpoch === 0) return false;
		if (effectiveItemCount > 0) return true;
		return wh.done && !wh.loading;
	}, [
		establishmentReady,
		isLoadingList,
		hasListError,
		effectiveItemCount,
		listSettledEpoch,
		wh.done,
		wh.loading,
	]);

	const showNoWarehouseAlert = useMemo(
		() =>
			resolutionComplete &&
			!hasListError &&
			effectiveItemCount === 0 &&
			!wh.error &&
			Array.isArray(wh.list) &&
			wh.list.length === 0,
		[resolutionComplete, hasListError, effectiveItemCount, wh.error, wh.list],
	);

	const warehouseVerifyError = wh.error || null;

	const createEnabled = useMemo(
		() =>
			resolutionComplete &&
			!wh.error &&
			(effectiveItemCount > 0 || (Array.isArray(wh.list) && wh.list.length > 0)),
		[resolutionComplete, wh.error, effectiveItemCount, wh.list],
	);

	const refreshEnabled = resolutionComplete;

	return {
		resolutionComplete,
		showNoWarehouseAlert,
		warehouseVerifyError,
		warehouseCheckLoading: wh.loading,
		createEnabled,
		refreshEnabled,
	};
}
